/**
 * WidgetSelector - Premium Modal for adding widgets to the dashboard
 *
 * Features:
 * - Glassmorphism design
 * - Category sidebar with icons
 * - Widget cards with hover effects
 * - Search with instant filtering
 * - CASL-based filtering
 */

import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Fade,
  Backdrop,
  alpha,
  Badge,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Check as CheckIcon,
  GridView as GridViewIcon,
  People as PeopleIcon,
  Science as SamplesIcon,
  Settings as SystemIcon,
} from "@mui/icons-material";
import KDTypography from "components/KDTypography";
import { useDashboard } from "../context/DashboardContext";
import { DEFAULT_SIZES, getDynamicRegistry } from "../config/widgetRegistry";

// Category icons mapping
const CATEGORY_ICONS = {
  users: PeopleIcon,
  samples: SamplesIcon,
  system: SystemIcon,
};

// Category colors
const CATEGORY_COLORS = {
  users: "#3B82F6",
  samples: "#10B981",
  system: "#6366F1",
};

function WidgetCard({ widget, onAdd, isAdded }) {
  const [isHovered, setIsHovered] = useState(false);
  const CategoryIcon = CATEGORY_ICONS[widget.category] || GridViewIcon;
  const color = CATEGORY_COLORS[widget.category] || "#7C3AED";
  const size = DEFAULT_SIZES[widget.type] || { w: 3, h: 2 };

  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => !isAdded && onAdd(widget.id)}
      sx={{
        position: "relative",
        p: 2.5,
        borderRadius: "16px",
        cursor: isAdded ? "default" : "pointer",
        background: isHovered && !isAdded
          ? `linear-gradient(135deg, ${alpha(color, 0.15)} 0%, ${alpha(color, 0.05)} 100%)`
          : "rgba(255, 255, 255, 0.03)",
        border: isAdded
          ? `2px solid ${alpha("#10B981", 0.5)}`
          : isHovered
          ? `1px solid ${alpha(color, 0.5)}`
          : "1px solid rgba(255, 255, 255, 0.08)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        transform: isHovered && !isAdded ? "translateY(-4px) scale(1.02)" : "translateY(0) scale(1)",
        boxShadow: isHovered && !isAdded ? `0 20px 40px -10px ${alpha(color, 0.3)}, 0 0 0 1px ${alpha(color, 0.1)}` : "none",
        opacity: isAdded ? 0.6 : 1,
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.3)})`,
          opacity: isHovered ? 1 : 0,
          transition: "opacity 0.3s ease",
        },
      }}
    >
      {/* Add/Added badge */}
      <Box
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          width: 36,
          height: 36,
          borderRadius: "10px",
          background: isAdded
            ? "linear-gradient(135deg, #10B981 0%, #059669 100%)"
            : `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: `0 4px 12px ${alpha(isAdded ? "#10B981" : color, 0.4)}`,
          transition: "all 0.3s ease",
          transform: isHovered ? "scale(1.1) rotate(5deg)" : "scale(1) rotate(0deg)",
        }}
      >
        {isAdded ? <CheckIcon sx={{ color: "white", fontSize: 18 }} /> : <AddIcon sx={{ color: "white", fontSize: 18 }} />}
      </Box>

      {/* Widget Icon */}
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: "14px",
          background: `linear-gradient(135deg, ${alpha(color, 0.2)} 0%, ${alpha(color, 0.1)} 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2,
          border: `1px solid ${alpha(color, 0.2)}`,
        }}
      >
        <CategoryIcon sx={{ color, fontSize: 24 }} />
      </Box>

      <KDTypography variant="subtitle1" sx={{ color: "white", fontWeight: 700, mb: 0.5, fontSize: "0.95rem", lineHeight: 1.3 }}>
        {widget.title}
      </KDTypography>

      <KDTypography
        variant="body2"
        sx={{
          color: "rgba(255,255,255,0.5)",
          fontSize: "0.8rem",
          lineHeight: 1.4,
          mb: 2,
          minHeight: 38,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {widget.description}
      </KDTypography>

      {/* Meta row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            px: 1,
            py: 0.5,
            borderRadius: "6px",
            bgcolor: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <KDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.65rem", fontWeight: 500 }}>
            {widget.type.replace(/_/g, " ")}
          </KDTypography>
        </Box>
        <Box sx={{ px: 1, py: 0.5, borderRadius: "6px", bgcolor: "rgba(255,255,255,0.04)" }}>
          <KDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.65rem" }}>
            {size.w}×{size.h}
          </KDTypography>
        </Box>
      </Box>
    </Box>
  );
}

WidgetCard.propTypes = {
  widget: PropTypes.object.isRequired,
  onAdd: PropTypes.func.isRequired,
  isAdded: PropTypes.bool,
};

function CategoryItem({ category, isActive, onClick, count, color }) {
  const Icon = CATEGORY_ICONS[category] || GridViewIcon;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderRadius: "12px",
        cursor: "pointer",
        background: isActive ? `linear-gradient(135deg, ${alpha(color, 0.2)} 0%, ${alpha(color, 0.1)} 100%)` : "transparent",
        border: isActive ? `1px solid ${alpha(color, 0.3)}` : "1px solid transparent",
        transition: "all 0.2s ease",
        "&:hover": {
          background: isActive
            ? `linear-gradient(135deg, ${alpha(color, 0.2)} 0%, ${alpha(color, 0.1)} 100%)`
            : "rgba(255,255,255,0.05)",
        },
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "8px",
          background: isActive ? `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)` : "rgba(255,255,255,0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.2s ease",
        }}
      >
        <Icon sx={{ color: isActive ? "white" : "rgba(255,255,255,0.5)", fontSize: 16 }} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <KDTypography
          variant="body2"
          sx={{ color: isActive ? "white" : "rgba(255,255,255,0.7)", fontWeight: isActive ? 600 : 500, fontSize: "0.85rem", textTransform: "capitalize" }}
        >
          {category}
        </KDTypography>
      </Box>
      <Badge
        badgeContent={count}
        sx={{
          "& .MuiBadge-badge": {
            bgcolor: isActive ? color : "rgba(255,255,255,0.15)",
            color: isActive ? "white" : "rgba(255,255,255,0.7)",
            fontSize: "0.65rem",
            fontWeight: 600,
            minWidth: 20,
            height: 20,
          },
        }}
      />
    </Box>
  );
}

CategoryItem.propTypes = {
  category: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  count: PropTypes.number,
  color: PropTypes.string,
};

function WidgetSelector({ open, onClose }) {
  const { state, actions } = useDashboard();
  const { accessibleCategories, activeWidgets } = state;
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const accessibleCategorySet = useMemo(
    () => new Set((accessibleCategories || []).map((c) => c.category)),
    [accessibleCategories]
  );

  const accessibleWidgets = useMemo(() => {
    // Use dynamic registry that includes backend-discovered metrics
    const registry = getDynamicRegistry();
    const allWidgets = Object.values(registry);
    return allWidgets.filter((widget) => accessibleCategorySet.has(widget.category));
  }, [accessibleCategorySet]);

  const filteredWidgets = useMemo(() => {
    let widgets = accessibleWidgets;
    if (selectedCategory !== "all") {
      widgets = widgets.filter((w) => w.category === selectedCategory);
    }
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      widgets = widgets.filter(
        (w) =>
          w.title.toLowerCase().includes(searchLower) ||
          w.description.toLowerCase().includes(searchLower) ||
          w.category.toLowerCase().includes(searchLower)
      );
    }
    return widgets;
  }, [accessibleWidgets, selectedCategory, search]);

  const categoryCounts = useMemo(() => {
    const counts = { all: accessibleWidgets.length };
    accessibleWidgets.forEach((w) => {
      counts[w.category] = (counts[w.category] || 0) + 1;
    });
    return counts;
  }, [accessibleWidgets]);

  const categories = useMemo(() => Array.from(accessibleCategorySet), [accessibleCategorySet]);

  const handleAddWidget = (widgetId) => actions.addWidget(widgetId);

  const addedCount = activeWidgets?.length || 0;
  const notAddedCount = filteredWidgets.filter((w) => !activeWidgets?.includes(w.id)).length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: { timeout: 300, sx: { backdropFilter: "blur(8px)", bgcolor: "rgba(0,0,0,0.7)" } },
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90vw",
            maxWidth: 1100,
            height: "85vh",
            maxHeight: 750,
            bgcolor: "#0F0F1A",
            borderRadius: "24px",
            border: "1px solid rgba(139, 92, 246, 0.2)",
            boxShadow: "0 40px 80px -20px rgba(0,0,0,0.8), 0 0 0 1px rgba(139, 92, 246, 0.1)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 4,
              py: 3,
              borderBottom: "1px solid rgba(255,255,255,0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "linear-gradient(180deg, rgba(139, 92, 246, 0.08) 0%, transparent 100%)",
            }}
          >
            <Box>
              <KDTypography variant="h4" sx={{ color: "white", fontWeight: 700, letterSpacing: "-0.02em", mb: 0.5 }}>
                Widget Library
              </KDTypography>
              <KDTypography variant="body2" sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.9rem" }}>
                {notAddedCount} widgets available &bull; {addedCount} added to dashboard
              </KDTypography>
            </Box>
            <IconButton
              onClick={onClose}
              sx={{
                width: 44,
                height: 44,
                bgcolor: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "12px",
                color: "rgba(255,255,255,0.7)",
                transition: "all 0.2s ease",
                "&:hover": { bgcolor: "rgba(239, 68, 68, 0.2)", borderColor: "rgba(239, 68, 68, 0.3)", color: "#EF4444" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Body */}
          <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
            {/* Sidebar */}
            <Box sx={{ width: 240, borderRight: "1px solid rgba(255,255,255,0.08)", py: 2, px: 1.5, display: "flex", flexDirection: "column", gap: 0.5, overflowY: "auto", background: "rgba(0,0,0,0.2)" }}>
              <CategoryItem category="All Widgets" isActive={selectedCategory === "all"} onClick={() => setSelectedCategory("all")} count={categoryCounts.all} color="#7C3AED" />
              <Box sx={{ my: 1, borderTop: "1px solid rgba(255,255,255,0.08)" }} />
              {categories.map((cat) => (
                <CategoryItem
                  key={cat}
                  category={cat}
                  isActive={selectedCategory === cat}
                  onClick={() => setSelectedCategory(cat)}
                  count={categoryCounts[cat] || 0}
                  color={CATEGORY_COLORS[cat] || "#7C3AED"}
                />
              ))}
            </Box>

            {/* Main content */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              {/* Search bar */}
              <Box sx={{ px: 3, py: 2, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                <TextField
                  fullWidth
                  placeholder="Search widgets by name, description, or type..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "rgba(255,255,255,0.4)", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: search && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearch("")} sx={{ color: "rgba(255,255,255,0.4)" }}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "rgba(255,255,255,0.04)",
                      borderRadius: "12px",
                      fontSize: "0.9rem",
                      "& fieldset": { borderColor: "rgba(255,255,255,0.08)" },
                      "&:hover fieldset": { borderColor: "rgba(139, 92, 246, 0.3)" },
                      "&.Mui-focused fieldset": { borderColor: "#7C3AED", borderWidth: 1 },
                    },
                    "& .MuiOutlinedInput-input": { color: "white", py: 1.5, "&::placeholder": { color: "rgba(255,255,255,0.4)" } },
                  }}
                />
              </Box>

              {/* Widgets grid */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  px: 3,
                  py: 2,
                  "&::-webkit-scrollbar": { width: 8 },
                  "&::-webkit-scrollbar-track": { bgcolor: "rgba(255,255,255,0.02)" },
                  "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(255,255,255,0.1)", borderRadius: 4, "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } },
                }}
              >
                {filteredWidgets.length === 0 ? (
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", opacity: 0.5 }}>
                    <SearchIcon sx={{ fontSize: 48, color: "rgba(255,255,255,0.3)", mb: 2 }} />
                    <KDTypography variant="h6" sx={{ color: "rgba(255,255,255,0.5)" }}>No widgets found</KDTypography>
                    <KDTypography variant="body2" sx={{ color: "rgba(255,255,255,0.3)" }}>
                      Try adjusting your search or category filter
                    </KDTypography>
                  </Box>
                ) : (
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 2 }}>
                    {filteredWidgets.map((widget) => (
                      <WidgetCard key={widget.id} widget={widget} onAdd={handleAddWidget} isAdded={activeWidgets?.includes(widget.id)} />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              px: 4,
              py: 2,
              borderTop: "1px solid rgba(255,255,255,0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "rgba(0,0,0,0.3)",
            }}
          >
            <KDTypography variant="body2" sx={{ color: "rgba(255,255,255,0.4)" }}>
              Click on any widget to add it to your dashboard
            </KDTypography>
            <Box
              onClick={onClose}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: "10px",
                background: "linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: "0 4px 12px rgba(124, 58, 237, 0.4)",
                "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 20px rgba(124, 58, 237, 0.5)" },
              }}
            >
              <KDTypography variant="button" sx={{ color: "white", fontWeight: 600 }}>
                Done
              </KDTypography>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
}

WidgetSelector.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default WidgetSelector;
